import { useEffect, useRef, useState } from "react";
import { Calendar, CreditCard, Plus, Search, Users } from "lucide-react";
import bizApi from "@/lib/businessApi";
import { useBiz } from "@/lib/businessI18n";
import { Badge } from "./ui";

export default function CommandPalette({ onClose, onNavigate, onOpenMission }) {
  const { t } = useBiz();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const timer = setTimeout(() => {
      bizApi.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  const actions = [
    { icon: Plus, label: t("palette.new_mission"), run: () => onNavigate("new_mission") },
    { icon: Calendar, label: t("palette.open_today"), run: () => onNavigate("overview") },
    { icon: CreditCard, label: t("palette.find_invoice"), run: () => onNavigate("billing") },
  ];

  const pick = (run) => { onClose(); run(); };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-ink/40 p-4 pt-[15vh] backdrop-blur-[2px]"
      onClick={onClose} data-testid="command-palette">
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-ink/[0.06] px-5 py-4">
          <Search className="h-4 w-4 text-slate" strokeWidth={2} />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t("hub.search_placeholder")} data-testid="palette-input"
            className="flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-slate/50"
            onKeyDown={(e) => e.key === "Escape" && onClose()} />
          <kbd className="rounded-md border border-ink/10 bg-cloud px-1.5 py-0.5 text-[10px] font-semibold text-slate">ESC</kbd>
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-2">
          {!results && actions.map((a, i) => (
            <button key={i} type="button" onClick={() => pick(a.run)} data-testid={`palette-action-${i}`}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm text-ink transition-colors hover:bg-cloud">
              <a.icon className="h-4 w-4 text-miles" strokeWidth={1.5} /> {a.label}
            </button>
          ))}
          {results && (
            <>
              {results.guests.length > 0 && <p className="px-4 pb-1 pt-3 text-[10px] font-bold uppercase tracking-wider text-slate/60">{t("palette.guests")}</p>}
              {results.guests.map((g) => (
                <button key={g.id} type="button" onClick={() => pick(() => onNavigate("guests"))} data-testid={`palette-guest-${g.id}`}
                  className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-left hover:bg-cloud">
                  <Users className="h-3.5 w-3.5 text-slate" />
                  <span className="text-sm text-ink">{g.first_name} {g.last_name}</span>
                  <span className="text-xs text-slate">{g.phone}</span>
                </button>
              ))}
              {results.missions.length > 0 && <p className="px-4 pb-1 pt-3 text-[10px] font-bold uppercase tracking-wider text-slate/60">{t("palette.missions")}</p>}
              {results.missions.map((m) => (
                <button key={m.id} type="button" onClick={() => pick(() => onOpenMission(m.id))} data-testid={`palette-mission-${m.id}`}
                  className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-left hover:bg-cloud">
                  <span className="font-mono text-xs font-bold text-miles">{m.number}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">{m.guest_name || m.pickup}</span>
                  <Badge tone={m.status}>{t(`status.${m.status}`)}</Badge>
                </button>
              ))}
              {results.invoices.length > 0 && <p className="px-4 pb-1 pt-3 text-[10px] font-bold uppercase tracking-wider text-slate/60">{t("palette.invoices")}</p>}
              {results.invoices.map((inv) => (
                <button key={inv.id} type="button" onClick={() => pick(() => onNavigate("billing"))} data-testid={`palette-invoice-${inv.id}`}
                  className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-left hover:bg-cloud">
                  <CreditCard className="h-3.5 w-3.5 text-slate" />
                  <span className="font-mono text-xs font-bold text-ink">{inv.number}</span>
                  <span className="text-xs text-slate">{inv.customer?.name}</span>
                </button>
              ))}
              {results.guests.length + results.missions.length + results.invoices.length === 0 && (
                <p className="px-4 py-6 text-center text-xs text-slate" data-testid="palette-empty">{t("palette.empty")}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
